import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { readScheduleRows } from '../../realtime/useScheduleRealtime';
import { useRealtimeState } from '../../realtime/RealtimeProvider';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from "react-redux";
import { Table, Spinner, Alert, Form, Col, Row, Modal, Button } from "react-bootstrap";
import {
  fetchUserDropdowns,
  clearDropdowns,
  selectDropdownOptions,
} from "../../redux/slices/dropdownSlice";
import { selectApiUrls, resetApiUrls } from "../../redux/slices/apiUrlSlice";
import { clearJobDetails } from "../../redux/slices/jobDetailsSlice";
import { clearHasValues } from "../../redux/slices/hasValuesSlice";
import { clearApiStatus, updateApiStatus } from "../../redux/slices/apiStatusSlice";
import { setJobId } from "../../redux/slices/jobIdSlice";
import { useNavigate } from "react-router-dom";
import { postData, patchData } from '../../redux/slices/apiCallSlice';
import { getProjectId } from "../../utils/dataNormalization";
import { formatDateForForm, formatDateUnified } from "../../utils/dateUtils";
import { subtractBusinessDays } from "../../utils/dateCalculations";
import { calculateCsmEnclosureDueDates, CSM_CALCULATED_DUE_COLUMNS, getCsmDueDateSeverity } from "../../utils/csmEnclosureDueDates";
import { selectHolidays } from "../../redux/slices/holidaysSlice";
import MOSearchModal from "../JobSchedulePage/MOSearchModal";
import PdfViewerModal from "../common/PdfViewerModal";
import axiosInstance from "../../axios/axiosConfig";
import { fetchBackgroundData } from '../../redux/api/portalApi';
import { readNpSnapshot } from '../../redux/api/npSnapshot';
import { usePermissions } from "../../hooks/usePermissions";
import useLiveUpdates from "../../hooks/useLiveUpdates";

const CSM_LEAD_FIELDS = [
  "CSMEnclosureJobStatus",
  "CSMEnclosureDueDate",
];

const CSM_PREFLIGHT_FIELDS = [
  "CSMEnclosurePreflightStatus",
  "CSMRoofCncStatus",
  "CSMEnclosureCncStatus",
];

const CSM_ROOF_FIELDS = [
  "CSMRoofPrep",
  "CSMRoofFrame",
  "CSMRoofGlued",
];

const CSM_ENCLOSURE_FIELDS = [
  "CSMEnclosurePrep",
  "CSMEnclosureFrame",
  "CSMEnclosureGlued",
  "CSMEnclosurePanelsInstalled",
  "CSMEnclosurePanelsMarked",
  "CSMEnclosureInsulationWindPockets",
  "CSMEnclosureFrameFinish",
];

const CSM_PAINT_FIELDS = [
  "CSMRoofPaintPrep",
  "CSMRoofPrime",
  "CSMRoofPaint",
  "CSMEnclosurePanelPrep",
  "CSMEnclosurePanelPaint",
  "CSMEnclosureTrim",
];

const CSM_FINAL_ASSEMBLY_FIELDS = [
  "CSMRoofFinishingStatus",
  "CSMEnclosureHardwareStatus",
  "CSMEnclosureFinalAssemblyStatus",
];

const CSM_MILESTONE_STATUS_FIELDS = {
  preflight: ["CSMEnclosurePreflightStatus"],
  cnc: ["CSMRoofCncStatus", "CSMEnclosureCncStatus"],
  initialAssembly: [...CSM_ROOF_FIELDS, ...CSM_ENCLOSURE_FIELDS],
  finishing: [...CSM_PAINT_FIELDS, "CSMRoofFinishingStatus", "CSMEnclosureHardwareStatus"],
};

const CSM_DUE_DATE_THEMES = {
  complete: { backgroundColor: '#dff6dd', color: '#107c10', label: 'Section complete' },
  blocked: { backgroundColor: '#fde7e9', color: '#a4262c', label: 'Overdue or due in less than 24 hours' },
  warning: { backgroundColor: '#fff4ce', color: '#795e00', label: 'Due in less than 7 days' },
  severeWarning: { backgroundColor: '#fed9cc', color: '#8a3707', label: 'Due in less than 14 days' },
  good: { backgroundColor: '#dff6dd', color: '#107c10', label: 'Due in less than 30 days' },
};

const CSM_OTHER_FIELDS = [
  "CSMEnclosureRequestedAssemblyDate",
  "CSMFrameDeliveryStatus",
  "CSMFrameRequestedDeliveryDate",
  "CSMWarranty",
  "CSMHardShip",
  "CSMNotes",
];
const CSM_TABLE_OTHER_FIELDS = [
  "CSMFrameDeliveryStatus",
  "CSMNotes",
];
const CSM_TABLE_ROW_ONE_FIELDS = [
  ...CSM_LEAD_FIELDS,
  ...CSM_PREFLIGHT_FIELDS,
  ...CSM_ROOF_FIELDS,
  ...CSM_ENCLOSURE_FIELDS,
  ...CSM_PAINT_FIELDS,
  ...CSM_FINAL_ASSEMBLY_FIELDS,
  ...CSM_TABLE_OTHER_FIELDS,
];

const CSM_ROW_ONE_FIELDS = [
  ...CSM_LEAD_FIELDS,
  ...CSM_PREFLIGHT_FIELDS,
  ...CSM_ROOF_FIELDS,
  ...CSM_ENCLOSURE_FIELDS,
  ...CSM_PAINT_FIELDS,
  ...CSM_FINAL_ASSEMBLY_FIELDS,
  ...CSM_OTHER_FIELDS,
];

const CSM_SUBCATEGORY_GROUPS = [
  { key: "preflight", title: "Preflight", fields: CSM_PREFLIGHT_FIELDS },
  { key: "roof", title: "Roof", fields: CSM_ROOF_FIELDS },
  { key: "enclosure", title: "Enclosure Frame", fields: CSM_ENCLOSURE_FIELDS },
  { key: "paint", title: "Paint", fields: CSM_PAINT_FIELDS },
  { key: "finalAssembly", title: "Final Assembly", fields: CSM_FINAL_ASSEMBLY_FIELDS },
];

const CSM_FIELD_SECTION_KEY_MAP = CSM_SUBCATEGORY_GROUPS.reduce((accumulator, section) => {
  section.fields.forEach((fieldName) => {
    accumulator[fieldName] = section.key;
  });
  return accumulator;
}, {});

const CSM_SECTION_BOUNDARIES = CSM_SUBCATEGORY_GROUPS.reduce((accumulator, section) => {
  accumulator[section.key] = {
    first: section.fields[0],
    last: section.fields[section.fields.length - 1],
  };
  return accumulator;
}, {});

const CSM_TASK_STATUS_FIELDS = [
  ...CSM_PREFLIGHT_FIELDS,
  ...CSM_ROOF_FIELDS,
  ...CSM_ENCLOSURE_FIELDS,
  ...CSM_PAINT_FIELDS,
  ...CSM_FINAL_ASSEMBLY_FIELDS,
];

const CSM_DEFAULT_NOT_STARTED_STATUS_FIELDS = new Set([
  "CSMEnclosureJobStatus",
  ...CSM_TASK_STATUS_FIELDS,
]);

const SECTION_THEME = {
  preflight: {
    headerBg: "#e8edf7",
    cellBg: "#f7f9fd",
    border: "#8d9fc7",
    accent: "#384d82",
  },
  roof: {
    headerBg: "#dceefe",
    cellBg: "#f5fbff",
    border: "#74a9d8",
    accent: "#2d6fa3",
  },
  enclosure: {
    headerBg: "#f8e4cf",
    cellBg: "#fff8f1",
    border: "#d19758",
    accent: "#975b1f",
  },
  paint: {
    headerBg: "#e9f4df",
    cellBg: "#f7fbf2",
    border: "#8fbd6b",
    accent: "#4f7d2a",
  },
  finalAssembly: {
    headerBg: "#f1e4f8",
    cellBg: "#fbf6fd",
    border: "#b78ad0",
    accent: "#744091",
  },
};
const STATUS_PILL_THEME = {
  neutral: {
    backgroundColor: "#eceff3",
    borderColor: "#c9d1db",
    color: "#475467",
  },
  info: {
    backgroundColor: "#dff7f6",
    borderColor: "#9fd9d6",
    color: "#0f6b6b",
  },
  progress: {
    backgroundColor: "#dceefe",
    borderColor: "#9dc5ea",
    color: "#1d5f91",
  },
  upcoming: {
    backgroundColor: "#ece6ff",
    borderColor: "#c7b6ff",
    color: "#5b3da6",
  },
  warning: {
    backgroundColor: "#fff2cf",
    borderColor: "#efcf83",
    color: "#8b5e00",
  },
  success: {
    backgroundColor: "#dff3e4",
    borderColor: "#a8d5b5",
    color: "#216e39",
  },
  danger: {
    backgroundColor: "#fde7e9",
    borderColor: "#efb3ba",
    color: "#a1262c",
  },
};
const STATUS_PILL_BASE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "1.75rem",
  padding: "0.15rem 0.65rem",
  borderRadius: "999px",
  borderStyle: "solid",
  borderWidth: "1px",
  fontSize: "0.78rem",
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: "normal",
  textAlign: "center",
};

const includesAnyStatusToken = (normalizedValue, tokens = []) => (
  tokens.some((token) => normalizedValue.includes(token))
);

const getStatusPillTheme = (value) => {
  const normalizedValue = normalizeSearchValue(value);

  if (!normalizedValue) {
    return STATUS_PILL_THEME.neutral;
  }

  if (includesAnyStatusToken(normalizedValue, ["not started", "not-started"])) {
    return STATUS_PILL_THEME.danger;
  }

  if (includesAnyStatusToken(normalizedValue, ["cancel", "reject", "issue", "overdue", "late", "failed"])) {
    return STATUS_PILL_THEME.danger;
  }

  if (includesAnyStatusToken(normalizedValue, ["complete", "completed", "done", "shipped", "approved", "closed"])) {
    return STATUS_PILL_THEME.success;
  }

  if (includesAnyStatusToken(normalizedValue, ["in progress", "progress", "active", "started", "running"])) {
    return STATUS_PILL_THEME.progress;
  }

  if (includesAnyStatusToken(normalizedValue, ["upcoming", "ready", "scheduled"])) {
    return STATUS_PILL_THEME.upcoming;
  }

  if (includesAnyStatusToken(normalizedValue, ["ordered", "order", "preflight", "review"])) {
    return STATUS_PILL_THEME.info;
  }

  if (includesAnyStatusToken(normalizedValue, ["on hold", "hold", "blocked", "waiting", "pending"])) {
    return STATUS_PILL_THEME.warning;
  }

  if (includesAnyStatusToken(normalizedValue, ["queued", "queue", "to do", "todo", "new"])) {
    return STATUS_PILL_THEME.neutral;
  }

  return STATUS_PILL_THEME.neutral;
};

const renderStatusPill = (value) => {
  const displayValue = String(value ?? "").trim();

  if (!displayValue) {
    return "";
  }

  const theme = getStatusPillTheme(displayValue);

  return (
    <span
      title={displayValue}
      style={{
        ...STATUS_PILL_BASE_STYLE,
        backgroundColor: theme.backgroundColor,
        borderColor: theme.borderColor,
        color: theme.color,
      }}
    >
      {displayValue}
    </span>
  );
};

const getSectionKeyForField = (fieldName) => {
  return CSM_FIELD_SECTION_KEY_MAP[fieldName] || null;
};

const getSectionBoundaryStyle = (fieldName) => {
  const sectionKey = getSectionKeyForField(fieldName);
  if (!sectionKey) {
    return null;
  }

  const theme = SECTION_THEME[sectionKey];
  const boundaries = CSM_SECTION_BOUNDARIES[sectionKey] || {};
  const isFirstColumn =
    fieldName === boundaries.first;
  const isLastColumn =
    fieldName === boundaries.last;

  return {
    backgroundColor: theme.cellBg,
    ...(isFirstColumn ? { borderLeft: `3px solid ${theme.border}` } : {}),
    ...(isLastColumn ? { borderRight: `3px solid ${theme.border}` } : {}),
  };
};

const getSectionHeaderCellStyle = (fieldName) => {
  const sectionKey = getSectionKeyForField(fieldName);
  if (!sectionKey) {
    return undefined;
  }

  const theme = SECTION_THEME[sectionKey];

  return {
    ...getSectionBoundaryStyle(fieldName),
    backgroundColor: theme.headerBg,
    color: theme.accent,
  };
};

const getSectionGroupHeaderStyle = (sectionKey) => {
  const theme = SECTION_THEME[sectionKey];
  if (!theme) {
    return undefined;
  }

  return {
    backgroundColor: theme.headerBg,
    color: theme.accent,
    borderTop: `2px solid ${theme.border}`,
    borderBottom: `2px solid ${theme.border}`,
  };
};

const getModalSectionStyle = (sectionTitle) => {
  const section = CSM_SUBCATEGORY_GROUPS.find((group) => group.title === sectionTitle);
  const sectionKey = section?.key || null;

  if (!sectionKey) {
    return {
      wrapper: undefined,
      heading: undefined,
    };
  }

  const theme = SECTION_THEME[sectionKey];

  return {
    wrapper: {
      backgroundColor: theme.cellBg,
      border: `1px solid ${theme.border}`,
      borderLeft: `5px solid ${theme.border}`,
      borderRadius: "0.75rem",
      padding: "1rem",
    },
    heading: {
      color: theme.accent,
      borderBottom: `1px solid ${theme.border}`,
      paddingBottom: "0.5rem",
      marginBottom: "1rem",
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
  };
};

const NP_SCHEDULE_MODAL_JOB_DETAIL_FIELDS = [
  {
    key: "JobNumber",
    label: "Job Number",
    getValue: (job) => job?.JobNumber ?? "",
  },
  {
    key: "ProjectName",
    label: "Project Name",
    getValue: (job) => job?.ProjectName ?? "",
  },
  {
    key: "BaseWidth",
    label: "Width",
    getValue: (job) => job?.BaseWidth ?? "",
  },
  {
    key: "BaseLength",
    label: "Length",
    getValue: (job) => job?.BaseLength ?? "",
  },
  {
    key: "Color",
    label: "Color",
    getValue: (job) => job?.Color ?? "",
  },
  {
    key: "SnowWindRating",
    label: "Wind Rated",
    getValue: (job) => job?.SnowWindRating ?? "",
  },
  {
    key: "Insulated",
    label: "Insulated",
    getValue: (job) => {
      const acoustic = job?.AcousticInsulation ?? "";
      const thermal = job?.ThermalInsulation ?? "";

      if (acoustic && thermal) {
        return `${acoustic} / ${thermal}`;
      }

      return acoustic || thermal || "";
    },
  },
  {
    key: "ProjectManager",
    label: "Project Manager",
    getValue: (job) => job?.ProjectManager ?? "",
  },
  {
    key: "MechanicalEngineer",
    label: "Mechanical Engineer",
    getValue: (job) => job?.MechanicalEngineer ?? "",
  },
];
const NP_SCHEDULE_MODAL_JOB_DETAIL_FIELD_MAP = NP_SCHEDULE_MODAL_JOB_DETAIL_FIELDS.reduce(
  (accumulator, field) => {
    accumulator[field.key] = field;
    return accumulator;
  },
  {}
);
const NP_SCHEDULE_MODAL_JOB_DETAIL_ROWS = [
  [
    { key: "JobNumber", md: 4 },
    { key: "ProjectName", md: 8 },
  ],
  [
    { key: "BaseWidth", md: 6 },
    { key: "BaseLength", md: 6 },
  ],
  [
    { key: "Color", md: 4 },
    { key: "SnowWindRating", md: 4 },
    { key: "Insulated", md: 4 },
  ],
  [
    { key: "ProjectManager", md: 6 },
    { key: "MechanicalEngineer", md: 6 },
  ],
];
const PROJECT_NAME_COLUMN_STYLE = {
  minWidth: "280px",
  width: "280px",
  whiteSpace: "normal",
};
const STICKY_HEADER_ROW_ONE_HEIGHT = 42;
const STICKY_EXPAND_COL_WIDTH = 48;
const STICKY_JOB_NUMBER_COL_WIDTH = 130;
const STICKY_PROJECT_NAME_COL_WIDTH = 280;
const CSM_TABLE_SCROLL_CONTAINER_STYLE = {
  position: "relative",
  maxHeight: "78vh",
  overflowX: "auto",
  overflowY: "auto",
};

const STICKY_COLUMN_LEFT = {
  expand: 0,
  jobNumber: STICKY_EXPAND_COL_WIDTH,
  projectName: STICKY_EXPAND_COL_WIDTH + STICKY_JOB_NUMBER_COL_WIDTH,
};

const STICKY_HEADER_BASE_STYLE = {
  position: "sticky",
  backgroundColor: "#f8f9fa",
  zIndex: 8,
};

const STICKY_FIRST_HEADER_ROW_STYLE = {
  ...STICKY_HEADER_BASE_STYLE,
  top: 0,
  zIndex: 10,
};

const STICKY_SECOND_HEADER_ROW_STYLE = {
  ...STICKY_HEADER_BASE_STYLE,
  top: `${STICKY_HEADER_ROW_ONE_HEIGHT}px`,
  zIndex: 11,
};

const getStickyHeaderColumnStyle = (columnKey, extraStyle = {}) => ({
  ...STICKY_SECOND_HEADER_ROW_STYLE,
  left: STICKY_COLUMN_LEFT[columnKey] ?? 0,
  zIndex: 12,
  ...extraStyle,
});

const getStickyBodyColumnStyle = (columnKey, extraStyle = {}) => ({
  position: "sticky",
  left: STICKY_COLUMN_LEFT[columnKey] ?? 0,
  backgroundColor: "#fff",
  zIndex: 4,
  ...extraStyle,
});
const NP_SCHEDULE_MODAL_DATE_FIELDS = [
  "CSMEnclosureJobStatus",
  "CSMEnclosureDueDate",
  "CSMEnclosureRequestedAssemblyDate",
];
const NP_SCHEDULE_MODAL_GENERAL_FIELDS = [
  "CSMFrameRequestedDeliveryDate",
  "CSMFrameDeliveryStatus",
  "CSMWarranty",
  "CSMHardShip",
  "CSMNotes",
];

const NP_SCHEDULE_MODAL_SECTIONS = [
  { title: "", fields: NP_SCHEDULE_MODAL_DATE_FIELDS },
  ...CSM_SUBCATEGORY_GROUPS.map((section) => ({
    title: section.title,
    fields: section.fields,
  })),
  { title: "General", fields: NP_SCHEDULE_MODAL_GENERAL_FIELDS },
];

const NP_SCHEDULE_STATUS_OPTIONS = [
  "Upcoming",
  "Not Started",
  "Pending",
  "In Progress",
  "Completed",
  "On Hold",
  "Ordered",
  "Shipped",
];

const NP_SCHEDULE_ATTACHMENT_FIELD = "SharePointFileIds";
const NP_SHAREPOINT_ATTACHMENTS_ROOT_FOLDER = "NPAttachments";
const NP_SHAREPOINT_ATTACHMENTS_ENTITY_FOLDER = "npSchedule";
const NP_SCHEDULE_ATTACHMENT_MAX_COUNT = 2;
const NP_SCHEDULE_ATTACHMENT_ID_DELIMITER = "|";

const NP_SCHEDULE_BOOLEAN_FIELDS = new Set(["CSMWarranty", "CSMHardShip"]);
const NP_SCHEDULE_DATE_FIELDS = new Set(
  [
    ...CSM_ROW_ONE_FIELDS.filter((fieldName) => fieldName.endsWith("Date")),
    "MechanicalDue",
  ]
);
const NP_TASK_STATUS_FIELDS = new Set([
  ...CSM_TASK_STATUS_FIELDS,
]);
const NP_CSM_PREFLIGHT_FIELDS = new Set(["CSMEnclosurePreflightStatus"]);
const NP_DELIVERY_STATUS_FIELDS = new Set(["CSMFrameDeliveryStatus"]);
const DROPDOWN_TYPE_NP_TASK_STATUS = "NP Task Status";
const DROPDOWN_TYPE_NP_CSM_PREFLIGHT = "NP CSM Preflight";
const DROPDOWN_TYPE_NP_CSM_JOB_STATUS = "NP CSM Job Status";
const DROPDOWN_TYPE_NP_DELIVERY_STATUS = "NP Delivery Status";
const DATE_PLACEHOLDER_VALUES = new Set([
  "",
  "na",
  "n/a",
  "none",
  "null",
  "undefined",
  "tbd",
  "-",
  "--",
]);

const CSM_FIELD_LABEL_OVERRIDES = {
  CSMRoofCncStatus: "Roof CNC",
  CSMEnclosureCncStatus: "Enclosure CNC",
  CSMRoofPaintPrep: "Roof Prep",
  CSMRoofPrime: "Roof Prime",
  CSMRoofPaint: "Roof Paint",
  CSMEnclosurePanelPrep: "Panel & Doors Prep",
  CSMEnclosurePanelPaint: "Panel & Doors Paint",
  CSMEnclosureTrim: "Frame Trim",
  CSMRoofFinishingStatus: "Shingles/ Finishing",
  CSMEnclosureHardwareStatus: "Enclosure Hardware",
  CSMEnclosureFinalAssemblyStatus: "Enclosure Final Assembly",
};

const normalizeSearchValue = (value) => String(value ?? "").trim().toLowerCase();

const CSM_ON_HOLD_ROW_STYLE = {
  backgroundColor: "#f8d7da",
};

const isHiddenCsmJobStatus = (value) => {
  const normalizedValue = normalizeSearchValue(value);

  return includesAnyStatusToken(normalizedValue, [
    "shipped",
    "canceled",
    "cancelled",
  ]);
};

const isOnHoldCsmJobStatus = (value) => normalizeSearchValue(value) === "on hold";

const getOnHoldRowCellStyle = (isOnHold, extraStyle = {}) => ({
  ...(isOnHold ? CSM_ON_HOLD_ROW_STYLE : {}),
  ...extraStyle,
});
const getDropdownDisplayText = (option) => (
  option?.DisplayText || option?.displayText || option?.Value || option?.value || ""
);

const normalizeDropdownTypeValue = (value) => String(value ?? "")
  .replace(/\s+/g, " ")
  .trim()
  .toLowerCase();

const getDropdownOptionsByType = (dropdownItems = [], dropdownType) => (
  (Array.isArray(dropdownItems) ? dropdownItems : [])
    .filter(
      (option) => normalizeDropdownTypeValue(option?.DropdownType || option?.dropdownType || "") ===
        normalizeDropdownTypeValue(dropdownType)
    )
    .sort((left, right) => (left?.SortOrder || 0) - (right?.SortOrder || 0))
);

const getUniqueDropdownOptionsByDisplayText = (dropdownItems = []) => {
  const seenValues = new Set();

  return (Array.isArray(dropdownItems) ? dropdownItems : []).filter((option) => {
    const displayText = getDropdownDisplayText(option);
    const normalizedValue = normalizeSearchValue(displayText);

    if (!normalizedValue) {
      return false;
    }

    if (seenValues.has(normalizedValue)) {
      return false;
    }

    seenValues.add(normalizedValue);
    return true;
  });
};

const isValueInDropdown = (value, dropdownValues = []) => {
  const normalizedValue = normalizeSearchValue(value);

  if (!normalizedValue) {
    return true;
  }

  return dropdownValues.some((option) => {
    const optionValue = option?.DisplayText ?? option?.displayText ?? option?.label ?? option?.value ?? option?.displayName;
    return normalizeSearchValue(optionValue) === normalizedValue;
  });
};

const isPlaceholderDateValue = (value) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value !== "string") {
    return false;
  }

  return DATE_PLACEHOLDER_VALUES.has(value.trim().toLowerCase());
};

const canAttemptDateParsing = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (isPlaceholderDateValue(trimmedValue)) {
      return false;
    }

    if (/^\d{4}-\d{2}-\d{2}(?:[ T].*)?$/.test(trimmedValue)) {
      return true;
    }

    return !Number.isNaN(Date.parse(trimmedValue));
  }

  const parsedDate = new Date(value);
  return !Number.isNaN(parsedDate.getTime());
};

const mergeUniqueOptions = (values = []) => {
  const optionsByNormalizedValue = new Map();

  values.forEach((value) => {
    if (value === null || value === undefined) {
      return;
    }

    const stringValue = String(value).trim();
    const normalizedValue = normalizeSearchValue(stringValue);
    if (!normalizedValue) {
      return;
    }

    if (!optionsByNormalizedValue.has(normalizedValue)) {
      optionsByNormalizedValue.set(normalizedValue, stringValue);
    }
  });

  return Array.from(optionsByNormalizedValue.values());
};

const toSortedOptionValues = (dropdownItems = []) =>
  [...dropdownItems]
    .sort((left, right) => (left?.SortOrder || 0) - (right?.SortOrder || 0))
    .map((item) => item?.DisplayText || item?.displayText || item?.Value || item?.value)
    .filter(Boolean);

const getNpScheduleProjectId = (record) => (
  record?.ProjectID ??
  record?.projectID ??
  record?.ProjectId ??
  record?.projectId ??
  null
);

const getNpCsmDueValue = (record) => (
  record?.NPCSMDue ??
  record?.npCsmDue ??
  record?.nPCSMDue ??
  record?.NpcsmDue ??
  record?.np_csm_due ??
  null
);

const parseEnhancedSectionValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    try {
      return JSON.parse(`[${trimmedValue}]`);
    } catch (arrayError) {
      console.warn("Unable to parse np-enhanced section payload:", arrayError);
      return null;
    }
  }
};

const toEnhancedSectionArray = (value) => {
  const parsedValue = parseEnhancedSectionValue(value);
  if (!parsedValue) {
    return [];
  }

  return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
};

const normalizeNpEnhancedResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const candidateKeys = ["data", "items", "records", "result", "results"];

  for (const key of candidateKeys) {
    if (Array.isArray(response[key])) {
      return response[key];
    }
  }

  for (const key of candidateKeys) {
    const nestedValue = response[key];
    if (nestedValue && typeof nestedValue === "object") {
      return normalizeNpEnhancedResponse(nestedValue);
    }
  }

  if (
    response.GeneralProjectInfo ||
    response.GeneralStationInfo ||
    response.StationEnclosure ||
    response.NPSchedule
  ) {
    return [response];
  }

  return Object.values(response).filter((entry) => entry && typeof entry === "object");
};

const selectCsmStationEnclosure = (records = []) => (
  records.find((record) => record?.HasEnclosure === true && record?.EnclosureType === "CSM") ||
  records.find((record) => record?.HasEnclosure === true) ||
  records[0] ||
  null
);

const selectGeneralStation = (records = []) => (
  records.find((record) => record?.Color) ||
  records[0] ||
  null
);

const toPayloadProjectId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmedValue = String(value).trim();

  if (trimmedValue === "") {
    return null;
  }

  // np-schedule backend expects numeric ProjectId when possible.
  if (/^-?\d+$/.test(trimmedValue)) {
    return Number(trimmedValue);
  }

  return trimmedValue;
};

const getNpScheduleAttachmentIdsValue = (record) => (
  record?.[NP_SCHEDULE_ATTACHMENT_FIELD] ??
  record?.sharePointFileIds ??
  null
);

const parseSharePointFileIds = (value) => {
  const uniqueIds = new Set(
    String(value ?? "")
      .split(NP_SCHEDULE_ATTACHMENT_ID_DELIMITER)
      .map((entry) => entry.trim())
      .filter(Boolean)
  );

  return Array.from(uniqueIds);
};

const stringifySharePointFileIds = (ids = []) => {
  const normalizedIds = Array.from(new Set((Array.isArray(ids) ? ids : [])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean)));

  return normalizedIds.length > 0
    ? normalizedIds.join(NP_SCHEDULE_ATTACHMENT_ID_DELIMITER)
    : null;
};

const sanitizeAttachmentFileName = (fileName) => String(fileName ?? "")
  .trim()
  .replace(/[<>:"/\\|?*]+/g, "-")
  .replace(/\s+/g, " ");

const formatAttachmentFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
    return "-";
  }

  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getGraphItemDisplayUrl = (item) => (
  item?.webUrl || item?.["@microsoft.graph.downloadUrl"] || ""
);

const encodeGraphPathSegment = (value) => encodeURIComponent(String(value ?? "").trim());

const isViewableAttachmentFile = (fileName) => {
  const extension = String(fileName ?? "").split(".").pop()?.toLowerCase();
  return extension === "pdf";
};

function CsmEnclosureTable({ showDueDates = false }) {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const dropdownOptions = useSelector(selectDropdownOptions);
  const holidays = useSelector(selectHolidays);
  const navigate = useNavigate();
  const { canView, canEdit } = usePermissions();
  const canViewEnclosureTable = canView("user.csm-enclosure-table");
  const canEditEnclosureTable = canEdit("user.csm-enclosure-table");
  const jobClickTimeoutRef = useRef(null);

  useEffect(() => {
    dispatch(resetApiUrls());
    dispatch(clearApiStatus());
    dispatch(clearJobDetails());
    dispatch(clearHasValues());
    dispatch(clearDropdowns());

    if (accounts.length > 0) {
      dispatch(fetchUserDropdowns({ instance, accounts }));
    }
  }, [dispatch, instance, accounts]);

  useEffect(() => {
    return () => {
      if (jobClickTimeoutRef.current) {
        clearTimeout(jobClickTimeoutRef.current);
      }
    };
  }, []);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'NPCSMDue', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [npScheduleRows, setNpScheduleRows] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleJob, setSelectedScheduleJob] = useState(null);
  const [modalFormData, setModalFormData] = useState({});
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [modalError, setModalError] = useState("");
  const [showMOSearchModal, setShowMOSearchModal] = useState(false);
  const [isSavingMO, setIsSavingMO] = useState(false);
  const [moFeedback, setMoFeedback] = useState(null);
  const [attachmentItems, setAttachmentItems] = useState([]);
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [deletingAttachmentIds, setDeletingAttachmentIds] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [attachmentMessage, setAttachmentMessage] = useState("");
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [loadingAttachmentViewer, setLoadingAttachmentViewer] = useState(false);
  const [attachmentViewerUrl, setAttachmentViewerUrl] = useState("");
  const [attachmentViewerExternalUrl, setAttachmentViewerExternalUrl] = useState("");
  const [attachmentViewerTitle, setAttachmentViewerTitle] = useState("");
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [attachmentPickerItems, setAttachmentPickerItems] = useState([]);
  const [attachmentPickerJobLabel, setAttachmentPickerJobLabel] = useState("");

  const npSharePointSiteId = process.env.REACT_APP_SHAREPOINT_NP_SITE_ID;
  const npSharePointDriveId = process.env.REACT_APP_SHAREPOINT_NP_DRIVE_ID;
  const hasNpSharePointConfig = Boolean(npSharePointSiteId && npSharePointDriveId);

  const acquireGraphAccessToken = useCallback(async () => {
    const account = accounts?.[0];

    if (!instance || !account) {
      throw new Error("Unable to access SharePoint because no signed-in account is available.");
    }

    const tokenResponse = await instance.acquireTokenSilent({
      scopes: ["https://graph.microsoft.com/.default"],
      account,
    });

    return tokenResponse.accessToken;
  }, [accounts, instance]);

  const getAttachmentFolderSegments = useCallback((job) => {
    const recordId = String(job?.NpScheduleRecordID ?? "").trim();

    return [
      NP_SHAREPOINT_ATTACHMENTS_ROOT_FOLDER,
      NP_SHAREPOINT_ATTACHMENTS_ENTITY_FOLDER,
      recordId,
    ].filter(Boolean);
  }, []);

  const fetchGraphFolderChildren = useCallback(async (folderId, accessToken) => {
    const response = await axiosInstance.get(
      `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${folderId}/children?$top=200`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return Array.isArray(response.data?.value) ? response.data.value : [];
  }, [npSharePointDriveId, npSharePointSiteId]);

  const ensureAttachmentFolderId = useCallback(async (job, accessToken) => {
    const folderSegments = getAttachmentFolderSegments(job);
    let currentFolderId = "root";

    for (const folderName of folderSegments) {
      const children = await fetchGraphFolderChildren(currentFolderId, accessToken);
      const existingFolder = children.find((item) => item.folder && item.name === folderName);

      if (existingFolder) {
        currentFolderId = existingFolder.id;
        continue;
      }

      const createResponse = await axiosInstance.post(
        `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${currentFolderId}/children`,
        {
          name: folderName,
          folder: {},
          "@microsoft.graph.conflictBehavior": "fail",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      currentFolderId = createResponse.data?.id;
    }

    return currentFolderId;
  }, [fetchGraphFolderChildren, getAttachmentFolderSegments, npSharePointDriveId, npSharePointSiteId]);

  const fetchGraphItemById = useCallback(async (itemId, accessToken) => {
    const response = await axiosInstance.get(
      `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${itemId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  }, [npSharePointDriveId, npSharePointSiteId]);


  useEffect(() => {
    const loadAttachments = async () => {
      if (!showScheduleModal || !selectedScheduleJob) {
        return;
      }

      setAttachmentError("");
      setAttachmentMessage("");
      setSelectedAttachmentFiles([]);

      const fileIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(selectedScheduleJob));

      if (!hasNpSharePointConfig || fileIds.length === 0) {
        setAttachmentItems([]);
        setLoadingAttachments(false);
        return;
      }

      setLoadingAttachments(true);

      try {
        const accessToken = await acquireGraphAccessToken();
        const items = await Promise.all(fileIds.map(async (fileId) => {
          try {
            return await fetchGraphItemById(fileId, accessToken);
          } catch (error) {
            return {
              id: fileId,
              name: `Unavailable file (${fileId})`,
              unavailable: true,
              error: error?.message || "Failed to load attachment metadata.",
            };
          }
        }));

        setAttachmentItems(items);
      } catch (error) {
        console.error("Failed to load NP attachments:", error);
        setAttachmentItems([]);
        setAttachmentError(error?.message || "Failed to load attachments.");
      } finally {
        setLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [acquireGraphAccessToken, fetchGraphItemById, hasNpSharePointConfig, selectedScheduleJob, showScheduleModal]);

  const statusDropdownOptions = useMemo(() => {
    const dropdownStatusValues = toSortedOptionValues(
      (Array.isArray(dropdownOptions) ? dropdownOptions : []).filter((option) => {
        const dropdownType = String(option?.DropdownType || option?.dropdownType || "").trim();
        return ["Job Status", "Project Status", "Panel Review Status"].includes(dropdownType);
      })
    );

    return mergeUniqueOptions([...dropdownStatusValues, ...NP_SCHEDULE_STATUS_OPTIONS]).map((label) => ({
      ID: `status-${label}`,
      DisplayText: label,
    }));
  }, [dropdownOptions]);

  const npTaskStatusDropdownOptions = useMemo(() => {
    return getDropdownOptionsByType(dropdownOptions, DROPDOWN_TYPE_NP_TASK_STATUS);
  }, [dropdownOptions]);

  const npCsmPreflightDropdownOptions = useMemo(() => {
    return getDropdownOptionsByType(dropdownOptions, DROPDOWN_TYPE_NP_CSM_PREFLIGHT);
  }, [dropdownOptions]);

  const npCsmJobStatusDropdownOptions = useMemo(() => {
    return (Array.isArray(dropdownOptions) ? dropdownOptions : [])
      .filter((option) => String(option?.DropdownType || option?.dropdownType || "").trim() === DROPDOWN_TYPE_NP_CSM_JOB_STATUS)
      .sort((left, right) => (left?.SortOrder || 0) - (right?.SortOrder || 0));
  }, [dropdownOptions]);

  const npDeliveryStatusDropdownOptions = useMemo(() => {
    const deliveryStatusOptions = getDropdownOptionsByType(dropdownOptions, DROPDOWN_TYPE_NP_DELIVERY_STATUS);
    return getUniqueDropdownOptionsByDisplayText(deliveryStatusOptions);
  }, [dropdownOptions]);

  const fetchJobs = useCallback(async ({ snapshotOnly = false, signal, initial = false } = {}) => {
    if (!snapshotOnly) { setLoading(true); setError(null); }
    try {
      if (!apiUrls.npEnhanced) {
        return false;
      }

      if (!snapshotOnly) dispatch(updateApiStatus({ tableName: 'npEnhanced', status: 'pending' }));

      const npEnhancedData = snapshotOnly ? await dispatch(fetchBackgroundData({ url: apiUrls.npEnhanced, signal })) : await dispatch(readNpSnapshot(apiUrls.npEnhanced, { force: !initial })).unwrap();
      if (snapshotOnly) readScheduleRows(npEnhancedData);
      const enhancedRows = normalizeNpEnhancedResponse(npEnhancedData);
      const safeNpScheduleData = enhancedRows
        .map((entry) => parseEnhancedSectionValue(entry?.NPSchedule))
        .filter(Boolean);
      if (!snapshotOnly) setNpScheduleRows(safeNpScheduleData);

      const allResults = enhancedRows.map((entry) => {
        const project = parseEnhancedSectionValue(entry?.GeneralProjectInfo) || {};
        const stationEnclosureMatch = selectCsmStationEnclosure(toEnhancedSectionArray(entry?.StationEnclosure));
        const generalStationMatch = selectGeneralStation(toEnhancedSectionArray(entry?.GeneralStationInfo));
        const npScheduleMatch = parseEnhancedSectionValue(entry?.NPSchedule) || null;
        const projectId = (
          getProjectId(project) ??
          getProjectId(stationEnclosureMatch) ??
          getProjectId(generalStationMatch) ??
          getNpScheduleProjectId(npScheduleMatch)
        );

        const npScheduleFieldValues = CSM_ROW_ONE_FIELDS.reduce((accumulator, fieldName) => {
          accumulator[fieldName] = npScheduleMatch?.[fieldName] ?? null;
          return accumulator;
        }, {});

        return {
          ...project,
          ProjectID: projectId,
          ...(stationEnclosureMatch || {}),
          ...(generalStationMatch || {}),
          ...npScheduleFieldValues,
          NPCSMDue: getNpCsmDueValue(npScheduleMatch) ?? getNpCsmDueValue(project),
          [NP_SCHEDULE_ATTACHMENT_FIELD]: getNpScheduleAttachmentIdsValue(npScheduleMatch),
          NpScheduleRecordID: npScheduleMatch?.ID ?? npScheduleMatch?.id ?? null,
          NpScheduleProjectID: getNpScheduleProjectId(npScheduleMatch),
          NpScheduleHasMatch: Boolean(npScheduleMatch),
          NpScheduleMatchType: npScheduleMatch ? "npEnhanced" : null,
        };
      });

      const combinedData = allResults.map((result) => {
        const color = result.EnclosurePaintColor === "Same as Station"
          ? result.Color || "N/A"
          : result.EnclosurePaintColor || "N/A";

        return {
          ...result,
          Color: color
        };
      }).filter((result) => result.JobNumber !== "N/A" && result.ProjectName !== "N/A");

      if (snapshotOnly) return { jobs: combinedData, schedules: safeNpScheduleData };
      setJobs(combinedData);
      if (combinedData.length > 0) {
        dispatch(updateApiStatus({ tableName: 'npEnhanced', status: 'success' }));
      } else {
        setError("No jobs found");
        dispatch(updateApiStatus({ tableName: 'npEnhanced', status: 'no data' }));
      }
      return true;
    } catch (err) {
      if (snapshotOnly) throw err;
      console.error("Error fetching jobs:", err);
      setError("Failed to fetch jobs");
      dispatch(updateApiStatus({ tableName: 'npEnhanced', status: 'failed', error: err.message }));
      return false;
    } finally {
      if (!snapshotOnly) setLoading(false);
    }
  }, [
    apiUrls.npEnhanced,
    dispatch,
  ]);

  useEffect(() => {
    fetchJobs({ initial: true });
  }, [fetchJobs]);

  const { mode: realtimeMode } = useRealtimeState();
  useGuardedRealtimeList({ apiKey: 'npEnhanced', enabled: canViewEnclosureTable,
    blocked: loading || showScheduleModal || showMOSearchModal || isSavingModal || isSavingMO || isUploadingAttachments || deletingAttachmentIds.length > 0,
    additionalResources: ['portal:general-project-info', 'portal:station-enclosure', 'portal:general-station-info', 'portal:np-schedule'],
    load: ({ signal }) => fetchJobs({ snapshotOnly: true, signal }), decode: snapshot => snapshot,
    apply: snapshot => { setJobs(snapshot.jobs); setNpScheduleRows(snapshot.schedules); },
  });

  const live = useLiveUpdates({
    enabled: canViewEnclosureTable && !['domain', 'session'].includes(realtimeMode),
    type: 'np-schedule/changed',
    scope: apiUrls.npSchedule,
    blocked: loading || showScheduleModal || showMOSearchModal || isSavingModal || isSavingMO
      || isUploadingAttachments || deletingAttachmentIds.length > 0,
    refresh: fetchJobs,
    // MO records may have no project ID; their job number still identifies a change.
    validate: (action) => typeof action.payload.jobNumber === 'string' && action.payload.jobNumber.trim() !== '',
  });
  const { publish } = live;
  const publishCsmChange = useCallback((job) => publish({
    jobNumber: String(job.JobNumber ?? '').trim(),
    projectId: toPayloadProjectId(job.ProjectID ?? job.NpScheduleProjectID),
  }), [publish]);

  const persistAttachmentIds = useCallback(async (job, nextIds) => {
    if (!job?.NpScheduleRecordID) {
      throw new Error("Save the NP Schedule record before managing attachments.");
    }

    const nextValue = stringifySharePointFileIds(nextIds);

    await dispatch(patchData({
      url: apiUrls.npSchedule,
      data: {
        ID: job.NpScheduleRecordID,
        ProjectId: toPayloadProjectId(job.ProjectID ?? job.NpScheduleProjectID),
        [NP_SCHEDULE_ATTACHMENT_FIELD]: nextValue,
      },
      allowFullPayload: true,
      contract: {
        requestCase: "pascal",
        renameMap: {
          projectId: "ProjectId",
        },
      },
    })).unwrap();

    void publishCsmChange(job);

    setSelectedScheduleJob((previousJob) => {
      if (!previousJob) {
        return previousJob;
      }

      return {
        ...previousJob,
        [NP_SCHEDULE_ATTACHMENT_FIELD]: nextValue,
      };
    });

    setJobs((previousJobs) => previousJobs.map((jobEntry) => {
      if ((jobEntry.ProjectID || jobEntry.ID) !== (job.ProjectID || job.ID)) {
        return jobEntry;
      }

      return {
        ...jobEntry,
        [NP_SCHEDULE_ATTACHMENT_FIELD]: nextValue,
      };
    }));
  }, [apiUrls.npSchedule, dispatch, publishCsmChange]);


  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const toSortableDateValue = useCallback((dateValue) => {
    if (!canAttemptDateParsing(dateValue)) {
      return null;
    }

    const isoDate = formatDateUnified(dateValue, 'iso', { fallback: '' });
    if (!isoDate) {
      return null;
    }

    const [year, month, day] = isoDate.split('-').map((part) => parseInt(part, 10));
    const localDate = new Date(year, month - 1, day);
    return Number.isNaN(localDate.getTime()) ? null : localDate.getTime();
  }, []);

  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => {
      if (sortConfig.key === 'JobNumber') {
        const aValue = parseFloat(a[sortConfig.key]) || 0;
        const bValue = parseFloat(b[sortConfig.key]) || 0;
        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }

      if (sortConfig.key === 'BaseLength' || sortConfig.key === 'BaseWidth') {
        const aValue = parseFloat(a[sortConfig.key]) || 0;
        const bValue = parseFloat(b[sortConfig.key]) || 0;
        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }

      if (sortConfig.key === 'NPCSMDue') {
        const aTime = toSortableDateValue(getNpCsmDueValue(a));
        const bTime = toSortableDateValue(getNpCsmDueValue(b));

        // Handle null/invalid values - push them to the end
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;
        if (bTime === null) return -1;

        return sortConfig.direction === 'ascending' ? aTime - bTime : bTime - aTime;
      }

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [jobs, sortConfig, toSortableDateValue]);

  const filteredJobs = useMemo(() => {
    return sortedJobs.filter((job) => {
      const searchFilter = searchTerm
        ? job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const hasEnclosureFilter = job.HasEnclosure === true;
      const enclosureTypeFilter = job.EnclosureType === "CSM";
      const hideFromListFilter = job.HideFromList !== true;
      const scheduleOnlyFilter = job.ScheduleOnly !== "Yes";
      const activeJobStatusFilter = !isHiddenCsmJobStatus(job.JobStatus);

      return searchFilter &&
        hasEnclosureFilter &&
        enclosureTypeFilter &&
        hideFromListFilter &&
        scheduleOnlyFilter &&
        activeJobStatusFilter;
    });
  }, [
    sortedJobs,
    searchTerm,
  ]);

  const formatDate = (dateString) => {
    if (!canAttemptDateParsing(dateString)) {
      return "";
    }

    return formatDateUnified(dateString, 'local', { fallback: '' });
  };

  const getDefaultEnclosureDueDate = useCallback((job) => {
    return subtractBusinessDays(getNpCsmDueValue(job), 2, holidays) || "";
  }, [holidays]);

  const getCsmWorkflowFieldValue = useCallback((fieldName, values = {}) => {
    const rawValue = values?.[fieldName];

    if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
      return rawValue;
    }

    return CSM_DEFAULT_NOT_STARTED_STATUS_FIELDS.has(fieldName) ? "Not Started" : "";
  }, []);

  const isCsmWorkflowTaskComplete = useCallback((value) => {
    const normalizedValue = normalizeSearchValue(value);

    return includesAnyStatusToken(normalizedValue, [
      "complete",
      "completed",
      "done",
      "shipped",
      "approved",
      "closed",
    ]);
  }, []);

  const isCsmWorkflowTaskStarted = useCallback((value) => {
    const normalizedValue = normalizeSearchValue(value);

    if (!normalizedValue) {
      return false;
    }

    return !includesAnyStatusToken(normalizedValue, [
      "not started",
      "not-started",
      "upcoming",
      "queued",
      "queue",
      "to do",
      "todo",
      "new",
    ]);
  }, []);

  const hasIncompleteCsmWorkflowTask = useCallback((values, fields) => (
    fields.some((fieldName) => (
      !isCsmWorkflowTaskComplete(getCsmWorkflowFieldValue(fieldName, values))
    ))
  ), [getCsmWorkflowFieldValue, isCsmWorkflowTaskComplete]);

  const getDerivedCsmJobStatus = useCallback((values = {}) => {
    if (isCsmWorkflowTaskComplete(getCsmWorkflowFieldValue("CSMEnclosureFinalAssemblyStatus", values))) {
      return "Complete";
    }

    const hasStartedTask = CSM_TASK_STATUS_FIELDS.some((fieldName) => (
      isCsmWorkflowTaskStarted(getCsmWorkflowFieldValue(fieldName, values))
    ));

    if (!hasStartedTask) {
      return "Upcoming";
    }

    if (hasIncompleteCsmWorkflowTask(values, CSM_PREFLIGHT_FIELDS)) {
      return "Preflight";
    }

    if (hasIncompleteCsmWorkflowTask(values, [
      ...CSM_ROOF_FIELDS,
      ...CSM_ENCLOSURE_FIELDS,
    ])) {
      return "Assembly";
    }

    if (hasIncompleteCsmWorkflowTask(values, CSM_PAINT_FIELDS)) {
      return "Paint";
    }

    const shinglesFinishingComplete = isCsmWorkflowTaskComplete(
      getCsmWorkflowFieldValue("CSMRoofFinishingStatus", values)
    );
    const enclosureHardwareComplete = isCsmWorkflowTaskComplete(
      getCsmWorkflowFieldValue("CSMEnclosureHardwareStatus", values)
    );

    if (shinglesFinishingComplete && enclosureHardwareComplete) {
      return "Final Assembly";
    }

    return "Finishing";
  }, [
    getCsmWorkflowFieldValue,
    hasIncompleteCsmWorkflowTask,
    isCsmWorkflowTaskComplete,
    isCsmWorkflowTaskStarted,
  ]);

  const getDefaultCsmFieldValue = useCallback((fieldName, job) => {
    if (fieldName === "CSMEnclosureJobStatus") {
      return getDerivedCsmJobStatus(job);
    }

    if (fieldName === "CSMEnclosureDueDate") {
      return getDefaultEnclosureDueDate(job);
    }

    if (CSM_DEFAULT_NOT_STARTED_STATUS_FIELDS.has(fieldName)) {
      return "Not Started";
    }

    return "";
  }, [getDefaultEnclosureDueDate, getDerivedCsmJobStatus]);

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prevRows) => (
      prevRows.includes(rowId)
        ? prevRows.filter((id) => id !== rowId)
        : [...prevRows, rowId]
    ));
  };

  const handleOpenJob = (job) => {
    if (!canOpenPortalJob(job)) return;
    const jobId = job.ProjectID || job.ID;
    dispatch(setJobId(jobId));
    navigate(`/${jobId}/generalInfo`);
  };

  const getCsmColumnLabel = (fieldName) => {
    if (CSM_FIELD_LABEL_OVERRIDES[fieldName]) {
      return CSM_FIELD_LABEL_OVERRIDES[fieldName];
    }

    let label = fieldName.replace(/^CSM/, "");

    if (fieldName.startsWith("CSMRoof") || fieldName.startsWith("CSMEnclosure")) {
      label = label.replace(/^(Roof|Enclosure)+/, "");
    }

    if (!["CSMEnclosureJobStatus", "CSMFrameDeliveryStatus"].includes(fieldName)) {
      label = label.replace(/Status$/, "");
    }

    return label
      .replace(/([A-Z])/g, " $1")
      .trim()
      .replace(/\bCnc\b/g, "CNC");
  };

  const formatExpandedValue = (fieldName, value, job = null) => {
    if (fieldName === "CSMEnclosureJobStatus") {
      return renderStatusPill(getDerivedCsmJobStatus(job));
    }

    const resolvedValue =
      (value === null || value === undefined || value === "")
        ? getDefaultCsmFieldValue(fieldName, job)
        : value;

    if (typeof resolvedValue === "boolean") {
      return resolvedValue ? "Yes" : "No";
    }

    if (resolvedValue === null || resolvedValue === undefined || resolvedValue === "") {
      return "";
    }

    if (NP_SCHEDULE_DATE_FIELDS.has(fieldName)) {
      return formatDate(resolvedValue);
    }

    if (fieldName.endsWith("Status") || NP_TASK_STATUS_FIELDS.has(fieldName)) {
      return renderStatusPill(resolvedValue);
    }

    return resolvedValue;
  };

  const getModalJobDetailValue = (detailField, job) => {
    const rawValue = detailField.getValue(job);

    if (typeof rawValue === "boolean") {
      return rawValue ? "Yes" : "No";
    }

    return rawValue ?? "";
  };

  const getBooleanToggleDisplayLabel = (value) => {
    if (value === "true") {
      return "Yes";
    }

    if (value === "false") {
      return "No";
    }

    return "Not set";
  };

  const getModalInitialValue = (fieldName, rawValue) => {
    if (NP_SCHEDULE_BOOLEAN_FIELDS.has(fieldName)) {
      if (rawValue === true || String(rawValue).toLowerCase() === "true") {
        return "true";
      }

      if (rawValue === false || String(rawValue).toLowerCase() === "false") {
        return "false";
      }

      return "";
    }

    if (NP_SCHEDULE_DATE_FIELDS.has(fieldName)) {
      if (!canAttemptDateParsing(rawValue)) {
        return "";
      }

      return formatDateForForm(rawValue);
    }

    return rawValue ?? "";
  };

  const getModalValueForSave = (fieldName, rawValue) => {
    if (NP_SCHEDULE_BOOLEAN_FIELDS.has(fieldName)) {
      if (rawValue === "true") return true;
      if (rawValue === "false") return false;
      return null;
    }

    if (NP_SCHEDULE_DATE_FIELDS.has(fieldName)) {
      return rawValue || null;
    }

    if (fieldName === "CSMNotes") {
      return rawValue || null;
    }

    const trimmedValue = String(rawValue ?? "").trim();
    return trimmedValue === "" ? null : trimmedValue;
  };

  const buildNpSchedulePostPayload = (createPayload = {}) => (
    Object.entries(createPayload).reduce((accumulator, [key, value]) => {
      const normalizedValue = value === undefined ? null : value;

      if (key === "ProjectID") {
        // Backend model maps this field as ProjectId (not ProjectID).
        accumulator.ProjectId = toPayloadProjectId(normalizedValue);
        return accumulator;
      }

      accumulator[key] = normalizedValue;
      return accumulator;
    }, {})
  );

  const normalizePatchComparableValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (value instanceof Date) {
      return value.toISOString().split("T")[0];
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();

      if (trimmedValue === "") {
        return null;
      }

      if (trimmedValue === "true") {
        return true;
      }

      if (trimmedValue === "false") {
        return false;
      }

      if (/^\d{4}-\d{2}-\d{2}/.test(trimmedValue)) {
        const normalizedDate = formatDateUnified(trimmedValue, 'iso', { fallback: '' });
        if (normalizedDate) {
          return normalizedDate;
        }
      }

      return trimmedValue;
    }

    if (typeof value === "number" && Number.isNaN(value)) {
      return null;
    }

    return value;
  };

  const arePatchValuesEquivalent = (leftValue, rightValue) => (
    normalizePatchComparableValue(leftValue) === normalizePatchComparableValue(rightValue)
  );

  const npScheduleFieldOptions = useMemo(() => {
    const optionsByField = {};

    CSM_ROW_ONE_FIELDS.forEach((fieldName) => {
      const optionValues = new Set();

      npScheduleRows.forEach((record) => {
        const recordValue = record?.[fieldName];
        if (recordValue === null || recordValue === undefined || recordValue === "") {
          return;
        }

        if (NP_SCHEDULE_DATE_FIELDS.has(fieldName)) {
          if (!canAttemptDateParsing(recordValue)) {
            return;
          }

          const normalizedDate = formatDateForForm(recordValue);
          if (normalizedDate) {
            optionValues.add(normalizedDate);
          }
          return;
        }

        if (NP_SCHEDULE_BOOLEAN_FIELDS.has(fieldName)) {
          optionValues.add(String(recordValue).toLowerCase() === "true" ? "true" : "false");
          return;
        }

        optionValues.add(String(recordValue));
      });

      const currentValue = modalFormData[fieldName];
      if (currentValue) {
        optionValues.add(currentValue);
      }

      optionsByField[fieldName] = mergeUniqueOptions(Array.from(optionValues));
    });

    return optionsByField;
  }, [modalFormData, npScheduleRows]);

  const handleOpenScheduleModal = (job) => {
    const initialFormData = CSM_ROW_ONE_FIELDS.reduce((accumulator, fieldName) => {
      accumulator[fieldName] = getModalInitialValue(fieldName, job?.[fieldName]);
      return accumulator;
    }, {});

    initialFormData.CSMEnclosureJobStatus = getDerivedCsmJobStatus({
      ...job,
      ...initialFormData,
    });

    // Default enclosure due date from CSM due date minus 2 working days unless already set.
    if (!initialFormData.CSMEnclosureDueDate) {
      const defaultEnclosureDueDate = getDefaultEnclosureDueDate(job);
      if (defaultEnclosureDueDate) {
        initialFormData.CSMEnclosureDueDate = defaultEnclosureDueDate;
      }
    }

    // Default blank Roof/Enclosure status values unless already set.
    CSM_DEFAULT_NOT_STARTED_STATUS_FIELDS.forEach((fieldName) => {
      if (!String(initialFormData[fieldName] ?? "").trim()) {
        initialFormData[fieldName] = getDefaultCsmFieldValue(fieldName, job);
      }
    });

    setSelectedScheduleJob(job);
    setModalFormData(initialFormData);
    setModalError("");
    setShowScheduleModal(true);
  };

  const handleCloseScheduleModal = (forceClose = false) => {
    if (isSavingModal && !forceClose) {
      return;
    }

    setShowScheduleModal(false);
    setSelectedScheduleJob(null);
    setModalFormData({});
    setModalError("");
    setAttachmentItems([]);
    setSelectedAttachmentFiles([]);
    setAttachmentError("");
    setAttachmentMessage("");
  };

  const handleModalFieldChange = (fieldName, nextValue) => {
    setModalFormData((previousValues) => {
      const nextValues = {
        ...previousValues,
        [fieldName]: nextValue,
      };

      nextValues.CSMEnclosureJobStatus = getDerivedCsmJobStatus({
        ...selectedScheduleJob,
        ...nextValues,
      });

      return nextValues;
    });
  };

  const handleAttachmentFileSelection = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    const currentCount = attachmentItems.length;

    setAttachmentError("");
    setAttachmentMessage("");

    if (currentCount + nextFiles.length > NP_SCHEDULE_ATTACHMENT_MAX_COUNT) {
      setSelectedAttachmentFiles([]);
      setAttachmentError(`A maximum of ${NP_SCHEDULE_ATTACHMENT_MAX_COUNT} attachments is allowed per item.`);
      event.target.value = "";
      return;
    }

    setSelectedAttachmentFiles(nextFiles);
  };

  const handleCloseAttachmentViewer = useCallback(() => {
    setShowAttachmentViewer(false);
    setAttachmentViewerUrl((previousUrl) => {
      if (previousUrl && previousUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return "";
    });
    setAttachmentViewerExternalUrl("");
    setAttachmentViewerTitle("");
    setLoadingAttachmentViewer(false);
  }, []);

  const handleCloseAttachmentPicker = useCallback(() => {
    setShowAttachmentPicker(false);
    setAttachmentPickerItems([]);
    setAttachmentPickerJobLabel("");
  }, []);

  const handleOpenAttachmentInViewer = useCallback(async (item, providedAccessToken = null) => {
    const targetUrl = getGraphItemDisplayUrl(item);

    if (!targetUrl) {
      setAttachmentError("Unable to open attachment because no SharePoint URL is available.");
      return;
    }

    setAttachmentError("");
    setAttachmentViewerTitle(item?.name || "Attachment");
    setAttachmentViewerExternalUrl(targetUrl);
    setAttachmentViewerUrl((previousUrl) => {
      if (previousUrl && previousUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return "";
    });
    setLoadingAttachmentViewer(true);
    setShowAttachmentViewer(true);

    if (!isViewableAttachmentFile(item?.name)) {
      setLoadingAttachmentViewer(false);
      return;
    }

    try {
      const accessToken = providedAccessToken || await acquireGraphAccessToken();
      const graphContentUrl = `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${item?.id}/content`;

      const response = await axiosInstance.get(graphContentUrl, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const objectUrl = URL.createObjectURL(response.data);
      setAttachmentViewerUrl(objectUrl);
    } catch (error) {
      console.error("Failed to load attachment preview:", error);
      setAttachmentViewerUrl("");
      setAttachmentError(error?.message || "Failed to load attachment preview. Use Open in New Tab as fallback.");
    } finally {
      setLoadingAttachmentViewer(false);
    }
  }, [acquireGraphAccessToken, npSharePointDriveId, npSharePointSiteId]);

  const handleViewRowAttachments = useCallback(async (event, job) => {
    event.stopPropagation();

    const fileIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(job));
    if (fileIds.length === 0) {
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    try {
      const accessToken = await acquireGraphAccessToken();
      const fetchedItems = await Promise.all(fileIds.map(async (fileId) => {
        try {
          return await fetchGraphItemById(fileId, accessToken);
        } catch (_) {
          return null;
        }
      }));

      const validItems = fetchedItems.filter((item) => item && getGraphItemDisplayUrl(item));
      if (validItems.length === 0) {
        setAttachmentError("Unable to load attachment metadata for this row.");
        return;
      }

      if (validItems.length > 1) {
        setAttachmentPickerItems(validItems);
        setAttachmentPickerJobLabel(
          String(job?.JobNumber ?? "").trim() || String(job?.ProjectName ?? "").trim() || "Selected Row"
        );
        setShowAttachmentPicker(true);
        return;
      }

      const preferredItem = validItems.find((item) => isViewableAttachmentFile(item?.name)) || validItems[0];
      await handleOpenAttachmentInViewer(preferredItem, accessToken);
    } catch (error) {
      console.error("Failed to load row attachments:", error);
      setAttachmentError(error?.message || "Failed to load row attachments.");
    }
  }, [acquireGraphAccessToken, fetchGraphItemById, handleOpenAttachmentInViewer, hasNpSharePointConfig]);

  const handleSelectAttachmentFromPicker = useCallback(async (item) => {
    handleCloseAttachmentPicker();
    await handleOpenAttachmentInViewer(item);
  }, [handleCloseAttachmentPicker, handleOpenAttachmentInViewer]);

  useEffect(() => {
    return () => {
      if (attachmentViewerUrl && attachmentViewerUrl.startsWith("blob:")) {
        URL.revokeObjectURL(attachmentViewerUrl);
      }
    };
  }, [attachmentViewerUrl]);

  const handleUploadAttachments = async () => {
    if (!canEditEnclosureTable) {
      return;
    }

    if (!selectedScheduleJob?.NpScheduleRecordID) {
      setAttachmentError("Save the NP Schedule record before adding attachments.");
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    if (selectedAttachmentFiles.length === 0) {
      setAttachmentError("Select at least one file to upload.");
      return;
    }

    setIsUploadingAttachments(true);
    setAttachmentError("");
    setAttachmentMessage("");

    try {
      const accessToken = await acquireGraphAccessToken();
      const parentFolderId = await ensureAttachmentFolderId(selectedScheduleJob, accessToken);
      const existingIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(selectedScheduleJob));
      const uploadedItems = [];

      for (const file of selectedAttachmentFiles) {
        const storedFileName = `${Date.now()}-${sanitizeAttachmentFileName(file.name)}`;
        const uploadResponse = await axiosInstance.put(
          `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${parentFolderId}:/${encodeGraphPathSegment(storedFileName)}:/content`,
          file,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": file.type || "application/octet-stream",
            },
          }
        );

        uploadedItems.push(uploadResponse.data);
      }

      const nextIds = [...existingIds, ...uploadedItems.map((item) => item?.id).filter(Boolean)];

      await persistAttachmentIds(selectedScheduleJob, nextIds);
      setAttachmentItems((previousItems) => [...previousItems, ...uploadedItems]);
      setSelectedAttachmentFiles([]);
      setAttachmentMessage(`${uploadedItems.length} attachment${uploadedItems.length === 1 ? "" : "s"} uploaded.`);
      await fetchJobs();
    } catch (error) {
      console.error("Failed to upload NP attachments:", error);
      setAttachmentError(error?.message || "Failed to upload attachments.");
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const handleDeleteAttachment = async (itemId) => {
    if (!canEditEnclosureTable) {
      return;
    }

    if (!selectedScheduleJob?.NpScheduleRecordID) {
      setAttachmentError("Save the NP Schedule record before removing attachments.");
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    setDeletingAttachmentIds((previousIds) => [...previousIds, itemId]);
    setAttachmentError("");
    setAttachmentMessage("");

    try {
      const accessToken = await acquireGraphAccessToken();

      await axiosInstance.delete(
        `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const existingIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(selectedScheduleJob));
      const nextIds = existingIds.filter((existingId) => existingId !== itemId);

      await persistAttachmentIds(selectedScheduleJob, nextIds);
      setAttachmentItems((previousItems) => previousItems.filter((item) => item?.id !== itemId));
      setAttachmentMessage("Attachment removed.");
      await fetchJobs();
    } catch (error) {
      console.error("Failed to delete NP attachment:", error);
      setAttachmentError(error?.message || "Failed to remove attachment.");
    } finally {
      setDeletingAttachmentIds((previousIds) => previousIds.filter((existingId) => existingId !== itemId));
    }
  };

  const getFieldStatusDropdownValues = (fieldName) => {
    if (fieldName === "CSMEnclosureJobStatus") {
      return npCsmJobStatusDropdownOptions;
    }

    if (fieldName.endsWith("Status") || NP_TASK_STATUS_FIELDS.has(fieldName)) {
      let preferredStatusOptions = statusDropdownOptions;

      if (NP_CSM_PREFLIGHT_FIELDS.has(fieldName) && npCsmPreflightDropdownOptions.length > 0) {
        preferredStatusOptions = npCsmPreflightDropdownOptions;
      } else if (NP_TASK_STATUS_FIELDS.has(fieldName) && npTaskStatusDropdownOptions.length > 0) {
        preferredStatusOptions = npTaskStatusDropdownOptions;
      } else if (NP_DELIVERY_STATUS_FIELDS.has(fieldName) && npDeliveryStatusDropdownOptions.length > 0) {
        preferredStatusOptions = npDeliveryStatusDropdownOptions;
      }

      return preferredStatusOptions;
    }

    return [];
  };

  const handleOpenMOSearchModal = () => {
    if (!canEditEnclosureTable) {
      return;
    }

    setMoFeedback(null);
    setShowMOSearchModal(true);
  };

  const handleCloseMOSearchModal = () => {
    if (isSavingMO) {
      return;
    }

    setShowMOSearchModal(false);
  };

  const handleSelectMO = async (moData) => {
    const jobNumber = String(moData?.JobNumber ?? "").trim();
    const projectName = String(moData?.ProjectName ?? "").trim();

    if (!jobNumber) {
      setMoFeedback({
        variant: "danger",
        message: "Unable to add MO item because the selected record has no Job Number.",
      });
      return;
    }

    const hasExistingRecord = npScheduleRows.some(
      (record) => normalizeSearchValue(record?.JobNumber) === normalizeSearchValue(jobNumber)
    );

    if (hasExistingRecord) {
      setMoFeedback({
        variant: "info",
        message: `${jobNumber} is already in NP Schedule.`,
      });
      return;
    }

    setIsSavingMO(true);
    setMoFeedback(null);

    try {
      const createPayload = CSM_ROW_ONE_FIELDS.reduce((accumulator, fieldName) => {
        accumulator[fieldName] = null;
        return accumulator;
      }, {
        ProjectID: null,
        JobNumber: jobNumber,
        ProjectName: projectName || jobNumber,
      });

      CSM_DEFAULT_NOT_STARTED_STATUS_FIELDS.forEach((fieldName) => {
        createPayload[fieldName] = "Not Started";
      });

      createPayload.CSMEnclosureJobStatus = getDerivedCsmJobStatus(createPayload);

      // MO imports should default warranty to true.
      createPayload.CSMWarranty = true;

      const postPayload = buildNpSchedulePostPayload(createPayload);

      await dispatch(postData({
        url: apiUrls.npSchedule,
        data: postPayload,
        contract: {
          requestCase: "pascal",
          renameMap: {
            projectId: "ProjectId",
          },
        },
      })).unwrap();

      void publishCsmChange({ JobNumber: jobNumber });
      setMoFeedback({
        variant: "success",
        message: `Added ${jobNumber} to NP Schedule with Warranty set to Yes.`,
      });
      setShowMOSearchModal(false);

      await fetchJobs();
    } catch (err) {
      console.error("Error adding MO item to np-schedule:", err);
      setMoFeedback({
        variant: "danger",
        message: err?.message || `Failed to add ${jobNumber} to NP Schedule.`,
      });
    } finally {
      setIsSavingMO(false);
    }
  };

  const handleSaveScheduleModal = async () => {
    if (!canEditEnclosureTable || !selectedScheduleJob) {
      return;
    }

    setIsSavingModal(true);
    setModalError("");

    const payload = {
      ...(selectedScheduleJob.NpScheduleRecordID
        ? { ID: selectedScheduleJob.NpScheduleRecordID }
        : {}),
      ProjectId: toPayloadProjectId(selectedScheduleJob.ProjectID),
      JobNumber: selectedScheduleJob.JobNumber ?? null,
      ProjectName: selectedScheduleJob.ProjectName ?? null,
    };

    CSM_ROW_ONE_FIELDS.forEach((fieldName) => {
      payload[fieldName] = getModalValueForSave(fieldName, modalFormData[fieldName]);
    });

    payload.CSMEnclosureJobStatus = getDerivedCsmJobStatus({
      ...selectedScheduleJob,
      ...payload,
    });

    try {
      let shouldRefreshJobs = false;

      if (selectedScheduleJob.NpScheduleHasMatch) {
        const patchPayload = {
          ...(selectedScheduleJob.NpScheduleRecordID
            ? { ID: selectedScheduleJob.NpScheduleRecordID }
            : {}),
          ProjectId: toPayloadProjectId(
            selectedScheduleJob.ProjectID ?? selectedScheduleJob.NpScheduleProjectID
          ),
        };

        CSM_ROW_ONE_FIELDS.forEach((fieldName) => {
          const nextValue = payload[fieldName];

          if (!arePatchValuesEquivalent(nextValue, selectedScheduleJob?.[fieldName])) {
            patchPayload[fieldName] = nextValue;
          }
        });

        const changedPatchFields = Object.keys(patchPayload).filter(
          (fieldName) => fieldName !== "ID" && fieldName !== "ProjectId"
        );

        if (changedPatchFields.length > 0) {
          await dispatch(patchData({
            url: apiUrls.npSchedule,
            data: patchPayload,
            allowFullPayload: true,
            contract: {
              requestCase: "pascal",
              renameMap: {
                projectId: "ProjectId",
              },
            },
          })).unwrap();

          shouldRefreshJobs = true;
        }
      } else {
        const createPayload = { ...payload };
        delete createPayload.ID;

        const postPayload = buildNpSchedulePostPayload(createPayload);

        await dispatch(postData({
          url: apiUrls.npSchedule,
          data: postPayload,
          contract: {
            requestCase: "pascal",
            renameMap: {
              projectId: "ProjectId",
            },
          },
        })).unwrap();

        shouldRefreshJobs = true;
      }

      if (shouldRefreshJobs) {
        void publishCsmChange(selectedScheduleJob);
        await fetchJobs();
      }

      handleCloseScheduleModal(true);
    } catch (err) {
      console.error("Error saving np-schedule record:", err);
      setModalError(err?.message || "Failed to save np-schedule record");
    } finally {
      setIsSavingModal(false);
    }
  };

  const handleJobNumberClick = (job) => {
    if (jobClickTimeoutRef.current) {
      clearTimeout(jobClickTimeoutRef.current);
    }

    jobClickTimeoutRef.current = setTimeout(() => {
      handleOpenJob(job);
      jobClickTimeoutRef.current = null;
    }, 220);
  };

  const handleJobNumberDoubleClick = (event, job) => {
    event.stopPropagation();

    if (jobClickTimeoutRef.current) {
      clearTimeout(jobClickTimeoutRef.current);
      jobClickTimeoutRef.current = null;
    }

    handleOpenScheduleModal(job);
  };

  const expandedColumns = [
    { key: "JobStatus", label: "Job Status" },
    { key: "MechanicalDue", label: "Mechanical Due Date" },
    { key: "ModelNumber", label: "Model Number" },
    { key: "ProjectManager", label: "Project Manager" },
    { key: "MechanicalEngineer", label: "Mechanical Engineer" },
    { key: "CSMEnclosureRequestedAssemblyDate", label: "Requested Assembly Date" },
    { key: "CSMFrameRequestedDeliveryDate", label: "Frame Requested Delivery Date" },
    { key: "CSMWarranty", label: "Warranty" },
  ];

  const baseColumnCount = 11;
  const calculatedColumnCount = showDueDates ? CSM_CALCULATED_DUE_COLUMNS.length : 0;
  const primaryColSpan = 10 + CSM_TABLE_ROW_ONE_FIELDS.length + calculatedColumnCount;

  if (!canViewEnclosureTable) {
    return null;
  }

  return (
    <div className="m-4">
      {live.warning && <Alert variant="warning" dismissible onClose={live.dismissWarning}>{live.warning}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {moFeedback?.message && (
        <Alert
          variant={moFeedback.variant || "info"}
          dismissible
          onClose={() => setMoFeedback(null)}
          className="py-2"
        >
          {moFeedback.message}
        </Alert>
      )}
      <div className="mb-3 text-center"><h1>CSM Enclosure Table</h1></div>
      {showDueDates && (
        <p className="text-muted">
          Milestone due dates are calculated from CSM Due.
        </p>
      )}
      <Row className="mb-3">
        <Col lg="auto">
          <Form.Control
            type="text"
            placeholder="Search by Job Number or Project Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        {canEditEnclosureTable && <Col md="auto" className="ms-md-auto">
          <Button
            variant="outline-primary"
            onClick={handleOpenMOSearchModal}
            disabled={isSavingMO}
          >
            {isSavingMO ? "Adding MO..." : "Add MO To NP Schedule"}
          </Button>
        </Col>}
      </Row>
      <div className="table-responsive" style={CSM_TABLE_SCROLL_CONTAINER_STYLE}>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th
                colSpan={baseColumnCount + CSM_LEAD_FIELDS.length + calculatedColumnCount}
                style={STICKY_FIRST_HEADER_ROW_STYLE}
              ></th>
              {CSM_SUBCATEGORY_GROUPS.map((section) => (
                <th
                  key={section.key}
                  colSpan={section.fields.length}
                  className="text-center"
                  style={{
                    ...STICKY_FIRST_HEADER_ROW_STYLE,
                    ...getSectionGroupHeaderStyle(section.key),
                  }}
                >
                  {section.title}
                </th>
              ))}
              <th
                colSpan={CSM_TABLE_OTHER_FIELDS.length}
                style={STICKY_FIRST_HEADER_ROW_STYLE}
              ></th>
            </tr>
            <tr>
              <th
                style={getStickyHeaderColumnStyle("expand", {
                  width: `${STICKY_EXPAND_COL_WIDTH}px`,
                  minWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                  maxWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                })}
              ></th>
              <th
                onClick={() => handleSort('JobNumber')}
                style={getStickyHeaderColumnStyle("jobNumber", {
                  width: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                  minWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                  maxWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                })}
              >
                Job Number
              </th>
              <th
                onClick={() => handleSort('ProjectName')}
                style={getStickyHeaderColumnStyle("projectName", {
                  ...PROJECT_NAME_COLUMN_STYLE,
                  width: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                  minWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                  maxWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                })}
              >
                Project Name
              </th>
              <th style={STICKY_SECOND_HEADER_ROW_STYLE}>Att</th>
              <th onClick={() => handleSort('NPCSMDue')} style={STICKY_SECOND_HEADER_ROW_STYLE}>CSM Due</th>
              <th onClick={() => handleSort('BaseWidth')} style={STICKY_SECOND_HEADER_ROW_STYLE}>Width</th>
              <th onClick={() => handleSort('BaseLength')} style={STICKY_SECOND_HEADER_ROW_STYLE}>Length</th>
              <th onClick={() => handleSort('Color')} style={STICKY_SECOND_HEADER_ROW_STYLE}>Color</th>
              <th onClick={() => handleSort('SnowWindRating')} style={STICKY_SECOND_HEADER_ROW_STYLE}>Wind Rated</th>
              <th onClick={() => handleSort('Insulated')} style={STICKY_SECOND_HEADER_ROW_STYLE}>Insulated</th>
              <th onClick={() => handleSort('CoolingFans')} style={STICKY_SECOND_HEADER_ROW_STYLE}>Cooling Fans</th>
              {showDueDates && CSM_CALCULATED_DUE_COLUMNS.map((column) => (
                <th key={column.key} style={{ ...STICKY_SECOND_HEADER_ROW_STYLE, minWidth: '140px' }}>
                  {column.label}
                </th>
              ))}
              {CSM_TABLE_ROW_ONE_FIELDS.map((fieldName) => (
                <th
                  key={fieldName}
                  style={{
                    ...STICKY_SECOND_HEADER_ROW_STYLE,
                    ...getSectionHeaderCellStyle(fieldName),
                  }}
                >
                  {getCsmColumnLabel(fieldName)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map((job) => {
            const rowId = job.ProjectID || job.ID;
            const isExpanded = expandedRows.includes(rowId);
            const calculatedDueDates = showDueDates
              ? calculateCsmEnclosureDueDates(getNpCsmDueValue(job))
              : null;
            const rowAttachmentIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(job));
            const rowAttachmentCount = rowAttachmentIds.length;
            const isOnHoldJob = isOnHoldCsmJobStatus(job.JobStatus);
            const isEngineeringComplete =
              job.MechanicalDone &&
              job.MechanicalDone !== 'R' &&
              String(job.MechanicalDone).trim() !== '';

            return (
              <React.Fragment key={rowId}>
                <tr
                  className={isExpanded ? 'table-active' : ''}
                  style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}
                  onDoubleClick={() => handleOpenScheduleModal(job)}
                >
                  <td
                    className="text-center align-middle"
                    style={getStickyBodyColumnStyle("expand", {
                      width: `${STICKY_EXPAND_COL_WIDTH}px`,
                      minWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                      maxWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                      ...(isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : {}),
                    })}
                  >
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      onClick={() => toggleRowExpansion(rowId)}
                      onDoubleClick={(event) => event.stopPropagation()}
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? 'v' : '>'}
                    </button>
                  </td>
                  <td
                    onClick={canOpenPortalJob(job) ? () => handleJobNumberClick(job) : undefined}
                    onDoubleClick={(event) => handleJobNumberDoubleClick(event, job)}
                    style={{
                      ...getStickyBodyColumnStyle("jobNumber", {
                        width: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                        minWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                        maxWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                        ...(isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : {}),
                      }),
                      cursor: 'pointer',
                    }}
                    className="align-middle"
                  >
                    {isEngineeringComplete && (
                      <span
                        className="me-2"
                        style={{ color: 'green' }}
                        title="Engineering Complete"
                        aria-label="Engineering Complete"
                      >
                        ✓
                      </span>
                    )}
                    {job.JobNumber}
                  </td>
                  <td
                    className="align-middle"
                    style={getStickyBodyColumnStyle("projectName", {
                      ...PROJECT_NAME_COLUMN_STYLE,
                      width: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                      minWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                      maxWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                      ...(isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : {}),
                    })}
                  >
                    {job.ProjectName}
                  </td>
                  <td
                    className="text-center align-middle"
                    style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}
                  >
                    {rowAttachmentCount > 0 ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={(event) => handleViewRowAttachments(event, job)}
                        onDoubleClick={(event) => event.stopPropagation()}
                        title={`View attachment${rowAttachmentCount === 1 ? "" : "s"} (${rowAttachmentCount})`}
                        aria-label={`View attachment${rowAttachmentCount === 1 ? "" : "s"} (${rowAttachmentCount})`}
                      >
                        <i className="bi bi-paperclip" />
                      </Button>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{formatDate(getNpCsmDueValue(job))}</td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{job.BaseWidth}</td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{job.BaseLength}</td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{job.Color}</td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{job.SnowWindRating}</td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{`${job.AcousticInsulation} / ${job.ThermalInsulation}`}</td>
                  <td className="align-middle" style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}>{job.CoolingFans}</td>
                  {showDueDates && CSM_CALCULATED_DUE_COLUMNS.map((column) => {
                    const dueDate = calculatedDueDates[column.key];
                    const isComplete = !hasIncompleteCsmWorkflowTask(job, CSM_MILESTONE_STATUS_FIELDS[column.key]);
                    const severity = getCsmDueDateSeverity(dueDate, isComplete);
                    const theme = CSM_DUE_DATE_THEMES[severity];

                    return (
                      <td
                        key={`${rowId}-${column.key}`}
                        className="align-middle"
                        title={dueDate ? theme?.label || 'Due in 30 days or more' : undefined}
                        style={{
                          ...(isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : {}),
                          whiteSpace: 'nowrap',
                          ...(theme ? {
                            backgroundColor: theme.backgroundColor,
                            color: theme.color,
                            // Keep Bootstrap striping/hover overlays from obscuring the deadline color.
                            boxShadow: 'none',
                          } : {}),
                        }}
                      >
                        {dueDate && (
                          <span
                            aria-label={`${column.label}: ${formatDate(dueDate)}. ${theme?.label || 'Due in 30 days or more'}`}
                          >
                            {isComplete && <span aria-hidden="true">✓ </span>}
                            {formatDate(dueDate)}
                          </span>
                        )}
                      </td>
                    );
                  })}

                  {CSM_TABLE_ROW_ONE_FIELDS.map((fieldName) => (
                    <td
                      key={`${rowId}-${fieldName}`}
                      className="align-middle"
                      style={getOnHoldRowCellStyle(isOnHoldJob, {
                        ...(getSectionBoundaryStyle(fieldName) || {}),
                        ...(fieldName === "CSMNotes"
                          ? { minWidth: "360px", whiteSpace: "normal" }
                          : {}),
                      })}
                    >
                      {formatExpandedValue(fieldName, job[fieldName], job)}
                    </td>
                  ))}
                </tr>

                {isExpanded && (
                  <tr>
                    <td style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}></td>
                    <td
                      colSpan={primaryColSpan}
                      className="p-0"
                      style={isOnHoldJob ? CSM_ON_HOLD_ROW_STYLE : undefined}
                    >
                      <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                          <thead>
                            <tr>
                              {expandedColumns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              {expandedColumns.map((column) => (
                                <td
                                  key={`${rowId}-${column.key}`}
                                  style={
                                    column.key === "CSMNotes"
                                      ? { minWidth: "340px", whiteSpace: "normal" }
                                      : undefined
                                  }
                                >
                                  {formatExpandedValue(column.key, job[column.key], job)}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          </tbody>
        </Table>
      </div>

      <Modal
        show={showScheduleModal}
        onHide={() => handleCloseScheduleModal()}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            NP Schedule Editor: {selectedScheduleJob?.JobNumber || ""}
            {selectedScheduleJob?.ProjectName ? ` - ${selectedScheduleJob.ProjectName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && (
            <Alert variant="danger" className="py-2">
              {modalError}
            </Alert>
          )}

          <div className="mb-4">
            <h6 className="border-bottom pb-2 mb-3">Job Details</h6>
            {NP_SCHEDULE_MODAL_JOB_DETAIL_ROWS.map((detailRow, rowIndex) => (
              <Row key={`np-schedule-job-detail-row-${rowIndex}`} className="g-3 mb-1">
                {detailRow.map((detailFieldConfig) => {
                  const detailField = NP_SCHEDULE_MODAL_JOB_DETAIL_FIELD_MAP[detailFieldConfig.key];

                  if (!detailField) {
                    return null;
                  }

                  return (
                    <Col key={detailField.key} md={detailFieldConfig.md}>
                      <Form.Group controlId={`np-schedule-job-detail-${detailField.key}`}>
                        <Form.Label>{detailField.label}</Form.Label>
                        <Form.Control
                          type="text"
                          value={getModalJobDetailValue(detailField, selectedScheduleJob)}
                          disabled
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>
            ))}
          </div>

          {NP_SCHEDULE_MODAL_SECTIONS.map((section) => {
            const sectionStyles = getModalSectionStyle(section.title);

            return (
            <div key={section.title} className="mb-4" style={sectionStyles.wrapper}>
              <h6 style={sectionStyles.heading}>{section.title}</h6>
              <Row className="g-3">
                {section.fields.map((fieldName) => (
                  <Col
                    key={fieldName}
                    md={fieldName === "CSMNotes" ? 12 : 6}
                    lg={fieldName === "CSMNotes" ? 12 : NP_SCHEDULE_BOOLEAN_FIELDS.has(fieldName) ? 6 : 4}
                  >
                    <Form.Group controlId={`np-schedule-${fieldName}`}>
                      <Form.Label>{getCsmColumnLabel(fieldName)}</Form.Label>
                      {fieldName === "CSMNotes" ? (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={modalFormData[fieldName] ?? ""}
                          onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                          placeholder="Enter notes"
                          disabled={!canEditEnclosureTable}
                        />
                      ) : NP_SCHEDULE_DATE_FIELDS.has(fieldName) ? (
                        <Form.Control
                          type="date"
                          value={modalFormData[fieldName] ?? ""}
                          onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                          disabled={!canEditEnclosureTable}
                        />
                      ) : fieldName.endsWith("Status") || NP_TASK_STATUS_FIELDS.has(fieldName) ? (
                        (() => {
                          const dropdownValues = getFieldStatusDropdownValues(fieldName);
                          const isDerivedJobStatus = fieldName === "CSMEnclosureJobStatus";
                          const currentValue = isDerivedJobStatus
                            ? getDerivedCsmJobStatus({
                                ...selectedScheduleJob,
                                ...modalFormData,
                              })
                            : modalFormData[fieldName] ?? "";

                          return (
                            <>
                              <Form.Select
                                value={currentValue}
                                onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                                disabled={!canEditEnclosureTable || isDerivedJobStatus}
                              >
                                <option value="">Select...</option>
                                {dropdownValues.map((option) => {
                                  const optionLabel = getDropdownDisplayText(option);

                                  return (
                                    <option key={`${fieldName}-${option.ID || optionLabel}`} value={optionLabel}>
                                      {optionLabel}
                                    </option>
                                  );
                                })}
                                {!isValueInDropdown(currentValue, dropdownValues) && currentValue && (
                                  <option value={currentValue} style={{ color: "red" }}>
                                    *{currentValue}
                                  </option>
                                )}
                              </Form.Select>
                              {isDerivedJobStatus && (
                                <Form.Text className="text-muted">
                                  Job Status updates automatically from CSM task progress.
                                </Form.Text>
                              )}
                            </>
                          );
                        })()
                      ) : NP_SCHEDULE_BOOLEAN_FIELDS.has(fieldName) ? (
                        (() => {
                          const currentValue = modalFormData[fieldName] ?? "";
                          const isChecked = currentValue === "true";

                          return (
                            <div>
                              <Form.Check
                                type="switch"
                                id={`np-schedule-toggle-${fieldName}`}
                                checked={isChecked}
                                onChange={(event) => handleModalFieldChange(fieldName, event.target.checked ? "true" : "false")}
                                label={getBooleanToggleDisplayLabel(currentValue)}
                                disabled={!canEditEnclosureTable}
                              />
                              {currentValue === "" && (
                                <Form.Text className="text-muted">Currently not set. Toggling will save Yes or No.</Form.Text>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          <Form.Control
                            type="text"
                            list={`np-schedule-options-${fieldName}`}
                            value={modalFormData[fieldName] ?? ""}
                            onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                            placeholder={NP_SCHEDULE_DATE_FIELDS.has(fieldName)
                              ? "YYYY-MM-DD"
                              : "Select or type a value"}
                            disabled={!canEditEnclosureTable}
                          />
                          <datalist id={`np-schedule-options-${fieldName}`}>
                            {(npScheduleFieldOptions[fieldName] || []).map((optionValue) => (
                              <option key={`${fieldName}-${optionValue}`} value={optionValue} />
                            ))}
                          </datalist>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </div>
          )})}

          <div className="mb-2">
            <h6 className="border-bottom pb-2 mb-3">Attachments</h6>

            {!hasNpSharePointConfig && (
              <Alert variant="warning" className="py-2">
                NP SharePoint attachment configuration is missing. Set REACT_APP_SHAREPOINT_NP_SITE_ID and REACT_APP_SHAREPOINT_NP_DRIVE_ID to enable attachments.
              </Alert>
            )}

            {!selectedScheduleJob?.NpScheduleRecordID && hasNpSharePointConfig && (
              <Alert variant="info" className="py-2">
                Save the NP Schedule record once before adding attachments.
              </Alert>
            )}

            {attachmentError && (
              <Alert variant="danger" className="py-2">
                {attachmentError}
              </Alert>
            )}

            {attachmentMessage && (
              <Alert variant="success" className="py-2">
                {attachmentMessage}
              </Alert>
            )}

            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <Form.Group controlId="np-schedule-attachments" className="flex-grow-1 mb-0">
                <Form.Label>Add attachments</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleAttachmentFileSelection}
                  disabled={!canEditEnclosureTable || !hasNpSharePointConfig || !selectedScheduleJob?.NpScheduleRecordID || isUploadingAttachments}
                />
                <Form.Text className="text-muted">
                  Up to {NP_SCHEDULE_ATTACHMENT_MAX_COUNT} attachments per item. Current count: {attachmentItems.length}.
                </Form.Text>
              </Form.Group>
              <Button
                variant="outline-primary"
                onClick={handleUploadAttachments}
                disabled={
                  !hasNpSharePointConfig ||
                  !canEditEnclosureTable ||
                  !selectedScheduleJob?.NpScheduleRecordID ||
                  isUploadingAttachments ||
                  selectedAttachmentFiles.length === 0
                }
              >
                {isUploadingAttachments ? "Uploading..." : `Upload ${selectedAttachmentFiles.length > 0 ? `(${selectedAttachmentFiles.length})` : ""}`}
              </Button>
            </div>

            {loadingAttachments ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : attachmentItems.length > 0 ? (
              <Table bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Modified</th>
                    <th>Size</th>
                    <th style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attachmentItems.map((item) => {
                    const isDeleting = deletingAttachmentIds.includes(item?.id);

                    return (
                      <tr key={item?.id || item?.name}>
                        <td>{item?.name || "Unknown file"}</td>
                        <td>{item?.lastModifiedDateTime ? new Date(item.lastModifiedDateTime).toLocaleDateString() : "-"}</td>
                        <td>{formatAttachmentFileSize(item?.size)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenAttachmentInViewer(item)}
                              disabled={!getGraphItemDisplayUrl(item)}
                            >
                              View
                            </Button>
                            {canEditEnclosureTable && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteAttachment(item?.id)}
                                disabled={isDeleting || !item?.id}
                              >
                                {isDeleting ? "Removing..." : "Delete"}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted">No attachments.</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleCloseScheduleModal()}
            disabled={isSavingModal}
          >
            Cancel
          </Button>
          {canEditEnclosureTable && (
            <Button
              variant="primary"
              onClick={handleSaveScheduleModal}
              disabled={isSavingModal || !selectedScheduleJob}
            >
              {isSavingModal ? "Saving..." : "Save NP Schedule"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <MOSearchModal
        show={showMOSearchModal}
        onHide={handleCloseMOSearchModal}
        onSelectMO={handleSelectMO}
      />

      <Modal
        show={showAttachmentPicker}
        onHide={handleCloseAttachmentPicker}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Select Attachment{attachmentPickerJobLabel ? ` - ${attachmentPickerJobLabel}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: "120px" }}>Size</th>
                <th style={{ width: "90px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {attachmentPickerItems.map((item) => (
                <tr key={item?.id || item?.name}>
                  <td>{item?.name || "Unknown file"}</td>
                  <td>{formatAttachmentFileSize(item?.size)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleSelectAttachmentFromPicker(item)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAttachmentPicker}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <PdfViewerModal
        show={showAttachmentViewer}
        onHide={handleCloseAttachmentViewer}
        title={attachmentViewerTitle || "Attachment Viewer"}
        loading={loadingAttachmentViewer}
        viewerUrl={attachmentViewerUrl}
        externalUrl={attachmentViewerExternalUrl}
        dialogClassName="engineering-pdf-viewer-modal"
      />

      {loading && <Spinner animation="border" />}
    </div>
  );
}

export default CsmEnclosureTable;
